import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Bell, BookOpen, Menu, PanelLeftClose, PanelLeftOpen, Plus } from 'lucide-react';
import { ROUTES } from '../../app/config';
import { useCurrentUser } from '../../auth/useSession';
import { BREAKPOINTS, useMediaQuery } from '../../hooks/useMediaQuery';
import { useNotifications } from '../../services/notifications.service';
import { useRealtimeNotifications } from '../../services/realtime';
import { cn } from '../../lib/cn';
import { Button, Drawer } from '../ui';
import { Logo } from './Logo';
import { NotificationsPanel } from './NotificationsPanel';
import { SidebarContent } from './Sidebar';

/**
 * Chrome de la aplicacion.
 *
 *  - Escritorio (>= 1024px): sidebar completo, plegable a modo compacto.
 *  - Tablet (768-1023px): sidebar compacto, solo iconos.
 *  - Movil (< 768px): drawer lateral.
 */
export function AppLayout(): JSX.Element {
  const { data: user } = useCurrentUser();
  const location = useLocation();

  const isDesktop = useMediaQuery(BREAKPOINTS.desktop);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsedOnDesktop, setCollapsedOnDesktop] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  // En tablet el sidebar es compacto siempre; en escritorio, a eleccion.
  const compact = !isDesktop || collapsedOnDesktop;

  const { data: notifications } = useNotifications();

  /*
   * El canal de avisos vive aqui, en el marco comun: una sola conexion por
   * pestana, abierta mientras haya sesion y cerrada al salir. Si colgara de
   * una pantalla concreta, se abriria y cerraria al navegar.
   */
  useRealtimeNotifications();

  // Cambiar de ruta cierra los paneles flotantes.
  useEffect(() => {
    setDrawerOpen(false);
    setNotificationsOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen bg-background">
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 hidden overflow-hidden border-r border-border',
          'transition-all duration-200 md:block',
          compact ? 'w-[4.5rem]' : 'w-64',
        )}
      >
        <SidebarContent user={user} compact={compact} />
      </aside>

      {/* Movil: el mismo arbol de navegacion dentro de un drawer. */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} label="Menu de navegacion">
        <SidebarContent user={user} onNavigate={() => setDrawerOpen(false)} />
      </Drawer>

      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col transition-all duration-200',
          compact ? 'md:pl-[4.5rem]' : 'md:pl-64',
        )}
      >
        <header className="sticky top-0 z-20 flex h-16 shrink-0 items-center gap-2 border-b border-border bg-surface/85 px-4 backdrop-blur sm:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setDrawerOpen(true)}
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5" aria-hidden />
          </Button>

          <div className="md:hidden">
            <Logo compact />
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:inline-flex"
            onClick={() => setCollapsedOnDesktop((value) => !value)}
            aria-label={collapsedOnDesktop ? 'Expandir menu' : 'Contraer menu'}
          >
            {collapsedOnDesktop ? (
              <PanelLeftOpen className="h-5 w-5" aria-hidden />
            ) : (
              <PanelLeftClose className="h-5 w-5" aria-hidden />
            )}
          </Button>

          <div className="ml-auto flex items-center gap-2">
            {/*
              La ayuda va en la barra y no en el menu de la izquierda: ese menu
              es para los sitios donde esta TU trabajo, y la ayuda no es uno de
              ellos. Es un enlace normal porque el centro de ayuda monta su
              propio marco y se lee sin sesion.
            */}
            <a
              href={ROUTES.docs}
              title="Centro de ayuda"
              className="hidden items-center gap-1.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary sm:inline-flex"
            >
              <BookOpen className="h-4 w-4" aria-hidden />
              Ayuda
            </a>

            <Link to={ROUTES.newTicket} className="hidden sm:block">
              <Button size="sm" leftIcon={<Plus className="h-4 w-4" aria-hidden />}>
                Crear ticket
              </Button>
            </Link>

            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setNotificationsOpen((value) => !value)}
                aria-label="Notificaciones"
                aria-expanded={notificationsOpen}
              >
                <Bell className="h-5 w-5" aria-hidden />
                {notifications && notifications.unread > 0 ? (
                  <span className="absolute right-0.5 top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold text-white">
                    {notifications.unread > 9 ? '9+' : notifications.unread}
                  </span>
                ) : null}
              </Button>

              {notificationsOpen ? (
                <NotificationsPanel onClose={() => setNotificationsOpen(false)} />
              ) : null}
            </div>
          </div>
        </header>

        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
